import { ClientCategorisation } from '@/constants';
import { FundsOwnership } from '@/constants/institution';
import {
  INSTITUTION_DETAILS_VERSION,
  type InstitutionDetailsFuturesKey,
  type InstitutionDetailsOnboardingKey,
  type InstitutionDetailsPartial,
  type InstitutionDetailsV2CustomerFundsKey,
  type InstitutionDetailsV2FuturesKey,
  type InstitutionDetailsV2OnboardingKey,
  type InstitutionDetailsV2Partial,
  type InstitutionDetailsVersion,
  institutionDetailsFuturesSchema,
  institutionDetailsOnboardingSchema,
  institutionDetailsV2CustomerFundsSchema,
  institutionDetailsV2FuturesSchema,
  institutionDetailsV2OnboardingCompleteSchema,
  institutionDetailsV2OnboardingSchema,
} from '@/services/api/profile';

export const INSTITUTION_DETAILS_TYPE = {
  ONBOARDING: 'onboarding',
  FUTURES: 'futures',
} as const;

export type InstitutionDetailsType = (typeof INSTITUTION_DETAILS_TYPE)[keyof typeof INSTITUTION_DETAILS_TYPE];

export type InstitutionDetailsKeyForDetailsType<T extends InstitutionDetailsType> = T extends typeof INSTITUTION_DETAILS_TYPE.ONBOARDING
  ? InstitutionDetailsOnboardingKey | InstitutionDetailsV2OnboardingKey
  : T extends typeof INSTITUTION_DETAILS_TYPE.FUTURES
    ? InstitutionDetailsFuturesKey
    : never;

type InstitutionDetailsAnswers = InstitutionDetailsPartial | InstitutionDetailsV2Partial | null | undefined;

const ONBOARDING_DETAIL_KEYS = Object.keys(institutionDetailsOnboardingSchema.shape) as InstitutionDetailsOnboardingKey[];

const ONBOARDING_DETAILS_KEY_ROUTE_MAP = {
  incorporation: 'incorporation',
  branchOperations: 'branch-operations',
  company: 'company',
  regulatoryInformation: 'regulatory-information',
  authorisation: 'authorisation',
} as const satisfies Record<InstitutionDetailsOnboardingKey, string>;

const FUTURES_DETAIL_KEYS = Object.keys(institutionDetailsFuturesSchema.shape) as InstitutionDetailsFuturesKey[];

const FUTURES_DETAILS_KEY_ROUTE_MAP = {
  decisionMaker: 'decision-maker',
  execution: 'execution',
  clientCategorisation: 'client-categorisation',
  reporting: 'reporting',
} as const satisfies Record<InstitutionDetailsFuturesKey, string>;

const V2_ONBOARDING_DETAIL_KEYS = Object.keys(institutionDetailsV2OnboardingSchema.shape) as InstitutionDetailsV2OnboardingKey[];

const V2_ONBOARDING_DETAILS_KEY_ROUTE_MAP = {
  incorporation: 'incorporation',
  branchOperations: 'branch-operations',
  company: 'company',
  businessProfile: 'business-profile',
  ownership: 'ownership',
  regulatoryInformation: 'regulatory-information',
  amlCtfSanctions: 'aml-ctf-sanctions',
  complianceTraining: 'compliance-training',
  kycCdd: 'kyc-cdd',
  expectedActivity: 'expected-activity',
  taxSelfCertification: 'tax-self-certification',
  products: 'products',
  decisionMaker: 'decision-maker',
  execution: 'execution',
  clientCategorisation: 'client-categorisation',
  reporting: 'reporting',
  // Authorisation is entered through the review of the application
  authorisation: 'review',
} as const satisfies Record<InstitutionDetailsV2OnboardingKey, string>;

type InstitutionDetailsTypeKeys = { [K in InstitutionDetailsType]: ReadonlyArray<InstitutionDetailsKeyForDetailsType<K>> };

const DETAILS_TYPE_KEYS_MAP: Record<InstitutionDetailsVersion, InstitutionDetailsTypeKeys> = {
  [INSTITUTION_DETAILS_VERSION.V1]: {
    [INSTITUTION_DETAILS_TYPE.ONBOARDING]: ONBOARDING_DETAIL_KEYS,
    [INSTITUTION_DETAILS_TYPE.FUTURES]: FUTURES_DETAIL_KEYS,
  },
  [INSTITUTION_DETAILS_VERSION.V2]: {
    [INSTITUTION_DETAILS_TYPE.ONBOARDING]: V2_ONBOARDING_DETAIL_KEYS,
    // A firm that signed off without futures answers the futures sections afterwards, as a V1 firm does
    [INSTITUTION_DETAILS_TYPE.FUTURES]: FUTURES_DETAIL_KEYS,
  },
};

const DETAILS_TYPE_ROUTES_MAP = {
  [INSTITUTION_DETAILS_VERSION.V1]: {
    [INSTITUTION_DETAILS_TYPE.ONBOARDING]: ONBOARDING_DETAILS_KEY_ROUTE_MAP,
    [INSTITUTION_DETAILS_TYPE.FUTURES]: FUTURES_DETAILS_KEY_ROUTE_MAP,
  },
  [INSTITUTION_DETAILS_VERSION.V2]: {
    [INSTITUTION_DETAILS_TYPE.ONBOARDING]: V2_ONBOARDING_DETAILS_KEY_ROUTE_MAP,
    [INSTITUTION_DETAILS_TYPE.FUTURES]: FUTURES_DETAILS_KEY_ROUTE_MAP,
  },
};

const DETAILS_TYPE_SCHEMA_MAP = {
  [INSTITUTION_DETAILS_TYPE.ONBOARDING]: institutionDetailsOnboardingSchema,
  [INSTITUTION_DETAILS_TYPE.FUTURES]: institutionDetailsFuturesSchema,
};

const getDetailKeys = <T extends InstitutionDetailsType>(detailType: T, version: InstitutionDetailsVersion) => DETAILS_TYPE_KEYS_MAP[version][detailType];

export const getDetailsKeyIndex = <T extends InstitutionDetailsType>(detailType: T, version: InstitutionDetailsVersion, key: InstitutionDetailsKeyForDetailsType<T>): number => {
  const keyIndex = getDetailKeys(detailType, version).indexOf(key);
  if (keyIndex === -1) throw new Error(`Key ${key} not found for ${detailType} V${version}`);

  return keyIndex;
};

// Section by section in flow order: parsing the whole application reports the cross-section rules only once every section is valid
// Futures and customer funds sections are optional in the onboarding schema, so they are checked against their own required schemas
const getV2OnboardingDetailsKeySchema = (key: InstitutionDetailsV2OnboardingKey) => {
  if (key in institutionDetailsV2FuturesSchema.shape) return institutionDetailsV2FuturesSchema.shape[key as InstitutionDetailsV2FuturesKey];

  if (key in institutionDetailsV2CustomerFundsSchema.shape) return institutionDetailsV2CustomerFundsSchema.shape[key as InstitutionDetailsV2CustomerFundsKey];

  return institutionDetailsV2OnboardingSchema.shape[key];
};

export const getV2OnboardingApplicableDetailsKeys = (institutionDetails: InstitutionDetailsV2Partial | null | undefined) => {
  const isFuturesApplicable = institutionDetails?.products?.futures === 'YES';
  const isCustomerFundsApplicable = institutionDetails?.businessProfile?.fundsOwnership !== FundsOwnership.OWN;

  return V2_ONBOARDING_DETAIL_KEYS.filter((key) => {
    if (key in institutionDetailsV2FuturesSchema.shape) return isFuturesApplicable;

    if (key in institutionDetailsV2CustomerFundsSchema.shape) return isCustomerFundsApplicable;

    return true;
  });
};

export const getV2OnboardingDetailsKeyRoute = (onboardingPath: string, key: InstitutionDetailsV2OnboardingKey) => `${onboardingPath}/${V2_ONBOARDING_DETAILS_KEY_ROUTE_MAP[key]}`;

const getNextV2OnboardingDetailsKey = async (institutionDetails: InstitutionDetailsV2Partial | null | undefined): Promise<InstitutionDetailsV2OnboardingKey | undefined> => {
  for (const key of getV2OnboardingApplicableDetailsKeys(institutionDetails)) {
    const result = await getV2OnboardingDetailsKeySchema(key).safeParseAsync(institutionDetails?.[key]);
    if (!result.success) return key;
  }

  const result = await institutionDetailsV2OnboardingCompleteSchema.safeParseAsync(institutionDetails);
  if (result.success) return undefined;

  return (result.error.issues[0]?.path[0]?.toString() ?? V2_ONBOARDING_DETAIL_KEYS[0]) as InstitutionDetailsV2OnboardingKey;
};

export const getNextDetailsKey = async <T extends InstitutionDetailsType>(
  detailType: T,
  version: InstitutionDetailsVersion,
  institutionDetails: InstitutionDetailsAnswers,
): Promise<InstitutionDetailsKeyForDetailsType<T> | undefined> => {
  if (version === INSTITUTION_DETAILS_VERSION.V2 && detailType === INSTITUTION_DETAILS_TYPE.ONBOARDING) {
    return (await getNextV2OnboardingDetailsKey(institutionDetails as InstitutionDetailsV2Partial | null | undefined)) as InstitutionDetailsKeyForDetailsType<T> | undefined;
  }

  const detailKeys = DETAILS_TYPE_KEYS_MAP[version][detailType];
  const schema = DETAILS_TYPE_SCHEMA_MAP[detailType];
  const result = await schema.safeParseAsync(institutionDetails);
  if (result.success) return undefined;

  const firstError = result.error.issues[0];
  const nextDetailsKey = firstError?.path[0]?.toString();
  if (!nextDetailsKey) return detailKeys[0] as InstitutionDetailsKeyForDetailsType<T>;

  return nextDetailsKey as InstitutionDetailsKeyForDetailsType<T>;
};

export const getNextDetailsKeyRoute = async <T extends InstitutionDetailsType>(
  detailType: T,
  version: InstitutionDetailsVersion,
  institutionDetails: InstitutionDetailsAnswers,
): Promise<string | undefined> => {
  const nextDetailsKey = await getNextDetailsKey(detailType, version, institutionDetails);
  if (!nextDetailsKey) return undefined;

  return (DETAILS_TYPE_ROUTES_MAP[version][detailType] as Partial<Record<InstitutionDetailsKeyForDetailsType<T>, string>>)[nextDetailsKey];
};

export const getDetailsKeyProgress = <T extends InstitutionDetailsType>(
  detailType: T,
  version: InstitutionDetailsVersion,
  key: InstitutionDetailsKeyForDetailsType<T>,
  currentStep?: number,
  totalSteps?: number,
) => {
  const detailKeys = getDetailKeys(detailType, version);
  const keyIndex = getDetailsKeyIndex(detailType, version, key);
  const stepProgress = totalSteps && currentStep ? currentStep / totalSteps : 0;
  const progress = (keyIndex + stepProgress) / detailKeys.length;

  return Number(Math.min(progress, (keyIndex + 1) / detailKeys.length).toFixed(2));
};

const ALGORITHM_ID_LENGTH = 30;

export const generateAlgorithmId = (companyName: string) => {
  const prefix = companyName
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase()
    .slice(0, 8);
  const rest = crypto
    .randomUUID()
    .replace(/-/g, '')
    .toUpperCase()
    .slice(0, ALGORITHM_ID_LENGTH - prefix.length);

  return `${prefix}${rest}`;
};

/** Only a professional client may be assumed to understand the risks, so a regulated institution is exempt only on professional treatment */
export const isExemptFromAppropriatenessTest = (isRegulated: boolean | null, clientCategorisationTreatment: ClientCategorisation | null) =>
  !!isRegulated && clientCategorisationTreatment === ClientCategorisation.PROFESSIONAL;
